import React, { useState, useEffect } from 'react'
import { fetchWithCsrf } from '../utils/csrf'
import StarRating from './StarRating'
import BookReviewForm from './BookReviewForm'
import ConfirmDialog from './ConfirmDialog'
import Modal from './Modal'

interface User {
  id: number
  name: string
}

interface Review {
  id: number
  rating: number
  review_text: string | null
  user: User
  created_at: string
  updated_at: string
  can_edit: boolean
}

interface BookReviewsProps {
  bookPickId: number
  currentUserId: number
  onClose: () => void
}

export default function BookReviews({ bookPickId, currentUserId, onClose }: BookReviewsProps) {
  const [reviews, setReviews] = useState<Review[]>([])
  const [averageRating, setAverageRating] = useState<number | null>(null)
  const [reviewsCount, setReviewsCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [showReviewForm, setShowReviewForm] = useState(false)
  const [editingReview, setEditingReview] = useState<Review | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deletingReviewId, setDeletingReviewId] = useState<number | null>(null)

  useEffect(() => {
    fetchReviews()
  }, [bookPickId])

  const fetchReviews = async () => {
    try {
      const response = await fetch(`/api/v1/book_picks/${bookPickId}/reviews`)
      if (response.ok) {
        const data = await response.json()
        // Convert rating strings to numbers
        const reviewsWithNumbers = data.reviews.map((r: any) => ({
          ...r,
          rating: Number(r.rating)
        }))
        setReviews(reviewsWithNumbers)
        setAverageRating(data.average_rating ? Number(data.average_rating) : null)
        setReviewsCount(data.reviews_count)
      }
    } catch (err) {
      console.error('Failed to fetch reviews:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = (reviewId: number) => {
    setDeletingReviewId(reviewId)
    setShowDeleteConfirm(true)
  }

  const handleDeleteConfirmed = async () => {
    if (!deletingReviewId) return

    setShowDeleteConfirm(false)

    try {
      const response = await fetchWithCsrf(`/api/v1/book_picks/${bookPickId}/reviews/${deletingReviewId}`, {
        method: 'DELETE'
      })

      if (response.ok) {
        fetchReviews()
      }
    } catch (err) {
      console.error('Failed to delete review:', err)
    } finally {
      setDeletingReviewId(null)
    }
  }

  const userReview = reviews.find(r => r.user.id === currentUserId)
  const hasReviewed = !!userReview

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  if (loading) {
    return (
      <Modal isOpen={true} onClose={onClose} size="xl" showCloseButton={true}>
        <div className="p-8 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
        </div>
      </Modal>
    )
  }

  return (
    <>
      <Modal isOpen={true} onClose={onClose} size="xl" showCloseButton={true} className="overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Book Reviews</h2>
              {averageRating !== null && (
                <div className="flex items-center gap-3">
                  <StarRating rating={averageRating} size="md" />
                  <span className="text-lg font-semibold text-gray-700">
                    {averageRating.toFixed(1)}
                  </span>
                  <span className="text-gray-500">({reviewsCount} {reviewsCount === 1 ? 'review' : 'reviews'})</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 pb-6 pt-4">
            {/* Add/Edit Review Button */}
            <div className="mb-6">
              <button
                onClick={() => {
                  if (hasReviewed) {
                    setEditingReview(userReview)
                  }
                  setShowReviewForm(true)
                }}
                className="w-full py-3 px-4 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all shadow-lg shadow-indigo-500/30"
              >
                {hasReviewed ? 'Edit Your Review' : 'Write a Review'}
              </button>
            </div>

            {/* Reviews List */}
            {reviews.length === 0 ? (
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">No Reviews Yet</h3>
                <p className="text-gray-600">Be the first to share your thoughts!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {reviews.map(review => (
                  <div
                    key={review.id}
                    className="p-5 bg-gray-50 rounded-xl border border-gray-200"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-semibold text-gray-900">{review.user.name}</span>
                          {review.user.id === currentUserId && (
                            <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-semibold rounded">
                              You
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <StarRating rating={review.rating} size="sm" />
                          <span className="text-sm text-gray-600">{review.rating.toFixed(1)}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-500 mb-2">
                          {formatDate(review.created_at)}
                        </div>
                        {review.can_edit && (
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                setEditingReview(review)
                                setShowReviewForm(true)
                              }}
                              className="text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(review.id)}
                              className="text-xs text-red-600 hover:text-red-700 font-medium"
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    {review.review_text && (
                      <p className="text-gray-700 whitespace-pre-wrap">{review.review_text}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
      </Modal>

      {/* Review Form Modal */}
      {showReviewForm && (
        <BookReviewForm
          bookPickId={bookPickId}
          existingReview={editingReview || undefined}
          onClose={() => {
            setShowReviewForm(false)
            setEditingReview(null)
          }}
          onSuccess={() => {
            setShowReviewForm(false)
            setEditingReview(null)
            fetchReviews()
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <ConfirmDialog
          message="Are you sure you want to delete your review?"
          onConfirm={handleDeleteConfirmed}
          onCancel={() => {
            setShowDeleteConfirm(false)
            setDeletingReviewId(null)
          }}
          confirmText="Delete"
        />
      )}
    </>
  )
}
